import type { BadgeTone } from '@/components/common/AppBadgeList.vue'
import type { CouleurOption, Libelles, References } from '@/api/vues'

/**
 * Affichage des valeurs de champ — fonctions PURES, sans Vue ni réseau.
 *
 * Briques PARTAGÉES par les types de champ (`types/<type>/index.ts`), qui
 * composent leur formatage à partir d'elles : deux formatages d'une même
 * monnaie finissent toujours par différer d'une décimale. Le choix du
 * formatage selon le type, lui, appartient au registre (`types/`).
 */

/**
 * Libellé dans la langue demandée, avec repli sur le français (exigé par le
 * serveur, donc toujours présent).
 */
export function libelleDe(libelles: Partial<Libelles> | undefined, langue: string): string {
  if (!libelles) return ''
  const traduit = (libelles as Record<string, string | undefined>)[langue]
  return traduit && traduit.trim() !== '' ? traduit : (libelles.fr ?? '')
}

/** Textes de l'interface que le formatage cite (oui / non / sans nom / référence disparue). */
export interface TextesFormat {
  oui: string
  non: string
  sansNom: string
  compteInconnu: string
  groupeInconnu: string
}

/**
 * Les textes du formatage dans la langue courante — l'UNIQUE écriture : six
 * copies de cet objet vivaient dans six composants, et un texte ajouté devait
 * l'être dans chacune.
 */
export function textesFormat(t: (cle: string) => string): TextesFormat {
  return {
    oui: t('common.yes'),
    non: t('common.no'),
    sansNom: t('vues.sansNom'),
    compteInconnu: t('vues.compteInconnu'),
    groupeInconnu: t('vues.groupeInconnu'),
  }
}

export interface ContexteFormat {
  langue: string
  references: References
  textes: TextesFormat
}

/**
 * Date civile `AAAA-MM-JJ`, formatée SANS conversion de fuseau.
 *
 * `new Date('2026-03-01')` est minuit UTC : formaté à Madrid, c'est bien le
 * 1er mars ; formaté à Bogotá, c'est le 28 février. On construit donc l'instant
 * en UTC et on FORMATE en UTC : la date affichée est la date saisie, partout.
 */
export function formaterDate(valeur: string, langue: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valeur)
  if (!m) return valeur
  const instant = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  instant.setUTCFullYear(Number(m[1]))
  return new Intl.DateTimeFormat(langue, { dateStyle: 'medium', timeZone: 'UTC' }).format(instant)
}

/** Instant ISO, formaté dans le fuseau du NAVIGATEUR (celui de l'utilisateur). */
export function formaterInstant(valeur: string, langue: string): string {
  const instant = new Date(valeur)
  if (Number.isNaN(instant.getTime())) return valeur
  return new Intl.DateTimeFormat(langue, { dateStyle: 'medium', timeStyle: 'short' }).format(instant)
}

/**
 * Date d'une CELLULE, dite par rapport à aujourd'hui quand c'est ce qui se lit
 * vite : « Aujourd'hui, 18:16 », « Hier », « Demain, 08:30 » ; au-delà, le jour
 * et le mois, et l'année seulement si ce n'est pas l'année en cours. Une
 * colonne qui répétait « 28 oct. 2026, 18:16 » sur chaque ligne ne disait
 * rien de plus. La forme complète (`formaterDate` / `formaterInstant`) reste
 * celle des exports, de la recherche et de l'infobulle.
 *
 * `valeur` : date civile `AAAA-MM-JJ` (sans heure ni fuseau), ou instant ISO
 * lu dans le fuseau du navigateur. Illisible : rendue telle quelle.
 */
export function dateRelative(valeur: string, avecHeure: boolean, langue: string, maintenant: Date = new Date()): string {
  const civile = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valeur)
  let annee: number, mois: number, jour: number
  let instant: Date | null = null
  if (civile) {
    annee = Number(civile[1])
    mois = Number(civile[2]) - 1
    jour = Number(civile[3])
  } else {
    instant = new Date(valeur)
    if (Number.isNaN(instant.getTime())) return valeur
    annee = instant.getFullYear()
    mois = instant.getMonth()
    jour = instant.getDate()
  }
  // Écart en JOURS DE CALENDRIER (pas en tranches de 24 h) : 23 h 50 la veille
  // est « hier », même si moins de 24 h ont passé.
  const ecart = Math.round(
    (Date.UTC(annee, mois, jour) - Date.UTC(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate())) / 86_400_000,
  )
  let libelleJour: string
  if (Math.abs(ecart) <= 1) {
    const relatif = new Intl.RelativeTimeFormat(langue, { numeric: 'auto' }).format(ecart, 'day')
    libelleJour = relatif.charAt(0).toLocaleUpperCase(langue) + relatif.slice(1)
  } else {
    const date = new Date(Date.UTC(annee, mois, jour))
    date.setUTCFullYear(annee)
    libelleJour = new Intl.DateTimeFormat(langue, {
      day: 'numeric',
      month: 'short',
      ...(annee !== maintenant.getFullYear() ? { year: 'numeric' as const } : {}),
      timeZone: 'UTC',
    }).format(date)
  }
  if (!avecHeure || instant === null) return libelleJour
  const heure = new Intl.DateTimeFormat(langue, { hour: '2-digit', minute: '2-digit' }).format(instant)
  return `${libelleJour}, ${heure}`
}

export { formaterDuree } from '@/components/common/duree'

/** Numéro automatique avec son préfixe et ses zéros de tête : « CL-0042 ». */
export function formaterNumero(numero: number, prefixe = '', chiffres = 0): string {
  return `${prefixe}${String(numero).padStart(chiffres, '0')}`
}

/** Nombre localisé, avec un nombre fixe de décimales quand le champ en impose un. */
export function formaterNombre(valeur: number, langue: string, decimales: number | undefined): string {
  return new Intl.NumberFormat(langue, {
    minimumFractionDigits: decimales ?? 0,
    maximumFractionDigits: decimales ?? 10,
  }).format(valeur)
}

/** Nom d'un compte référencé ; « compte inconnu » s'il a disparu. */
export function nomCompte(id: string, contexte: ContexteFormat): string {
  const compte = contexte.references.utilisateurs[id]
  if (!compte) return contexte.textes.compteInconnu
  return compte.nom.trim() !== '' ? compte.nom : compte.email
}

/** Nom d'un groupe référencé ; « groupe inconnu » s'il a disparu. */
export function nomGroupe(id: string, contexte: ContexteFormat): string {
  return contexte.references.groupes[id]?.nom ?? contexte.textes.groupeInconnu
}

/** Libellé d'une fiche liée ; « sans nom » si son champ principal est vide. */
export function nomRecord(id: string, contexte: ContexteFormat): string {
  return contexte.references.records[id]?.libelle ?? contexte.textes.sansNom
}

/** Classe de pastille d'une couleur d'option (jetons de `assets/main.css`). */
export function classeCouleur(couleur: string): string {
  switch (couleur) {
    case 'primaire':
      return 'badge-primary'
    case 'succes':
      return 'badge-success'
    case 'attention':
      return 'badge-warn'
    case 'danger':
      return 'badge-danger'
    default:
      return ''
  }
}

/** Ton de pastille (`AppBadgeList`) d'une couleur d'option : même table que `classeCouleur`. */
export function toneCouleur(couleur: CouleurOption): BadgeTone {
  switch (couleur) {
    case 'primaire':
      return 'primary'
    case 'succes':
      return 'success'
    case 'attention':
      return 'warn'
    case 'danger':
      return 'danger'
    default:
      return 'neutral'
  }
}
